import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["clearableValues", "multiCollapseLimit", "allRecipients", "onChange", "commonMessageReply"];
import { useCallback, useContext } from 'react';
import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { validateEmail } from 'composer-data/email/public/operators';
// @ts-expect-error module not typed
import { setCommonMessage } from 'conversations-thread-data/common-reply/public/operators/commonReplySetters';
import { updateRecipientField } from 'conversations-thread-data/common-reply/public/operators/updateRecipientField';
import { TO as TO_RECIPIENTFIELD } from 'conversations-message-history/common-message-format/constants/recipientFields';
import { useEmailDeliverabilityStatuses } from 'composer-data/email/public/hooks';
import { useContactPermissions } from 'conversations-thread-data/contact-permissions/public/hooks';
import { getEmailAddressOptions } from '../operators/getEmailAddressOptions';
import CustomRecipientTag from './CustomRecipientTag';
import { logRecipientChange } from './Recipient';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';
import { useNeverLog } from '../../../email/protected/hooks';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDiv = styled.div.withConfig({
  displayName: "EmailTagDisplayBar__StyledDiv",
  componentId: "sc-13ihd6m-0"
})(["display:inline-flex;align-items:center;height:100%;width:100%;cursor:pointer;font-weight:normal;font-size:14px;overflow:hidden;color:", ";"], ({
  theme
}) => theme.color['icon-core-subtle']);

// Avoid 2 tooltips at the same time when UITag string is shrunk
const truncateStringProps = {
  tooltip: false
};
const MoreButton = ({
  numberOfEmails
}) => {
  return numberOfEmails > 0 ? /*#__PURE__*/_jsx(Button, {
    use: "transparent",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.metadata-editor.email-tag-display-bar.display-more-emails-button",
      options: {
        numberOfEmails
      }
    })
  }) : null;
};
MoreButton.displayName = 'MoreButton';
const EmailTag = ({
  option,
  deleteRecipient
}) => {
  const handleCloseClick = evt => deleteRecipient(evt, option.address);
  return /*#__PURE__*/_jsx(Tag, {
    use: option.tagUse,
    truncateStringProps: truncateStringProps,
    closeable: true,
    onCloseClick: handleCloseClick,
    children: /*#__PURE__*/_jsx(CustomRecipientTag, {
      option: option
    })
  }, option.address);
};
EmailTag.displayName = 'EmailTag';
const EmailTagDisplayBar = props => {
  const {
      clearableValues,
      multiCollapseLimit,
      allRecipients,
      onChange,
      commonMessageReply
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const emailContactValues = allRecipients.get('to') || ImmutableList();
  const allRecipientsSize = allRecipients.get('all').size;
  const emailAddresses = allRecipients.get('all').map(emailAddressRecord => emailAddressRecord.address).toArray();
  const {
    deliverabilityStatuses
  } = useEmailDeliverabilityStatuses({
    emailAddresses
  });
  const {
    contactPermissions
  } = useContactPermissions({
    emailAddresses: ImmutableSet(emailAddresses)
  });
  const getValidationData = emailAddress => {
    return validateEmail(emailAddress, deliverabilityStatuses, contactPermissions);
  };
  const {
    emailBlocklistStatus
  } = useNeverLog({
    commonMessage: commonMessageReply
  });
  const emailTagContent = getEmailAddressOptions(emailContactValues, {
    clearable: clearableValues,
    getValidationData,
    emailBlocklistStatus
  });
  const {
    trackEvent
  } = useContext(EditorTrackerContext);
  const deleteRecipient = useCallback((evt, address) => {
    const prevToRecipients = allRecipients.get('to');
    const nextToRecipients = prevToRecipients.filter(emailAddressRecord => emailAddressRecord.address !== address).toList();
    const updatedMessage = updateRecipientField(TO_RECIPIENTFIELD, nextToRecipients, commonMessageReply);

    // Update the reply
    const replyWithRecipients = setCommonMessage(updatedMessage);
    onChange(replyWithRecipients);
    logRecipientChange(nextToRecipients, prevToRecipients, 'to', trackEvent);

    // if the size of TO recipients is greater than 0, we don't want the
    // click event to propagate to EmailTagDisplayBar and expand all
    // fields. However when the size is 0 we want to expand the field and reveal
    // other recipients if any. Otherwise this results in collapsed metadata
    // displaying just '+x more' text without any recipient names
    if (nextToRecipients.size > 0) {
      evt.stopPropagation();
      evt.preventDefault();
    }
  }, [allRecipients, commonMessageReply, onChange, trackEvent]);
  const toRecipientSize = emailContactValues.size;
  const toRecipientOtherCount = Math.max(toRecipientSize - multiCollapseLimit, 0);
  const numberOfEmails = allRecipientsSize - (toRecipientSize - toRecipientOtherCount);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(StyledDiv, Object.assign({}, rest, {
      children: [allRecipientsSize ? emailTagContent.slice(0, multiCollapseLimit).map(emailOption => /*#__PURE__*/_jsx(EmailTag, {
        option: emailOption,
        deleteRecipient: deleteRecipient
      }, emailOption.address)) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.email-tag-display-bar.no-recipients"
      }), /*#__PURE__*/_jsx(MoreButton, {
        numberOfEmails: numberOfEmails
      })]
    }))
  );
};
EmailTagDisplayBar.displayName = 'EmailTagDisplayBar';
export default EmailTagDisplayBar;